/**
 * TopPostsWidget Component (Story 5.15, Task 3)
 *
 * Renders top performing social media posts in a responsive grid layout.
 * Follows the established 4-state pattern: Loading → Error → No Data → Data.
 *
 * Responsive grid: colSpan 4 → 1-col compact, colSpan 6 → 2-col, colSpan 12 → 3-col.
 * Minimum colSpan enforced at 4.
 */

import { useEffect, useState } from "react";
import {
  Heart,
  MessageCircle,
  Share2,
  Eye,
  MousePointer,
  Bookmark,
  Facebook,
  Linkedin,
  Instagram,
  AlertCircle,
  ExternalLink,
} from "lucide-react";
import type { WidgetConfig } from "../../../lib/templateUtils";
import type { WidgetDataEnvelope, AnyWidgetPayload, TopPostRecord } from "../../../lib/widgetData";
import { isTopPostsData } from "../../../lib/widgetData";
import { useChartWidgetShell, ChartMessage } from "./chartWidgetShell";

export interface TopPostsWidgetProps {
  widget: WidgetConfig;
  data?: WidgetDataEnvelope<AnyWidgetPayload>;
  onAutoResize?: (widgetId: string, newRowSpan: number) => void;
}

/**
 * Returns number of grid columns based on widget colSpan.
 * Exported for use in auto-resize computation.
 */
export function getPostGridCols(colSpan: number): 1 | 2 | 3 {
  if (colSpan >= 12) return 3;
  if (colSpan >= 6) return 2;
  return 1;
}

const PLATFORM_ICONS: Record<TopPostRecord["platform"], typeof Facebook> = {
  "facebook-pages": Facebook,
  "linkedin-pages": Linkedin,
  "instagram-business": Instagram,
};

const PLATFORM_LABELS: Record<TopPostRecord["platform"], string> = {
  "facebook-pages": "Facebook",
  "linkedin-pages": "LinkedIn",
  "instagram-business": "Instagram",
};

const POST_TYPE_LABELS: Record<TopPostRecord["postType"], string> = {
  image: "Image",
  video: "Video",
  link: "Link",
  text: "Text",
  carousel: "Carousel",
  reel: "Reel",
  unknown: "Post",
};

function formatMetricValue(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return value.toLocaleString("en-US");
}

function formatDate(isoDate: string): string {
  const date = new Date(isoDate);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}

function ThumbnailImage({
  post,
  compact,
}: {
  post: TopPostRecord;
  compact: boolean;
}) {
  const [imgError, setImgError] = useState(false);
  const PlatformIcon = PLATFORM_ICONS[post.platform];
  const size = compact ? "w-16 h-16" : "w-full aspect-square";

  if (!post.thumbnailUrl || imgError) {
    return (
      <div
        className={`${size} bg-muted flex items-center justify-center rounded shrink-0`}
        data-testid={imgError ? "post-thumbnail-fallback" : "post-thumbnail-placeholder"}
      >
        <PlatformIcon size={compact ? 20 : 32} className="text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className={`${size} relative overflow-hidden rounded shrink-0 bg-muted`}>
      <img
        src={post.thumbnailUrl}
        alt={`${PLATFORM_LABELS[post.platform]} post thumbnail`}
        className="w-full h-full object-cover"
        loading="lazy"
        onError={() => setImgError(true)}
      />
    </div>
  );
}

function PostCard({
  post,
  compact,
}: {
  post: TopPostRecord;
  compact: boolean;
}) {
  const PlatformIcon = PLATFORM_ICONS[post.platform];
  const platformLabel = PLATFORM_LABELS[post.platform];
  const isInstagram = post.platform === "instagram-business";
  const captionExcerpt = post.content.length > 120 ? post.content.slice(0, 120) + "..." : post.content;

  if (compact) {
    return (
      <div
        className="flex gap-3 rounded-lg border border-border bg-background p-2.5"
        data-testid={`post-card-${post.postId}`}
      >
        <ThumbnailImage post={post} compact />
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-center gap-1.5">
            <PlatformIcon size={12} className="shrink-0 text-muted-foreground" />
            <span className="text-[10px] text-muted-foreground">{platformLabel}</span>
            <span className="rounded bg-muted px-1 py-0.5 text-[10px] text-muted-foreground">
              {POST_TYPE_LABELS[post.postType]}
            </span>
          </div>
          <p className="mb-1 line-clamp-2 text-xs leading-snug" dir="auto">{captionExcerpt}</p>
          <div className="flex items-center gap-2.5 text-[10px] tabular-nums text-muted-foreground">
            <span className="flex items-center gap-0.5" title="Reactions"><Heart size={10} />{formatMetricValue(post.reactions)}</span>
            <span className="flex items-center gap-0.5" title="Comments"><MessageCircle size={10} />{formatMetricValue(post.comments)}</span>
            {isInstagram ? (
              <span className="flex items-center gap-0.5" title="Saves"><Bookmark size={10} />{formatMetricValue(post.saves ?? 0)}</span>
            ) : (
              <span className="flex items-center gap-0.5" title="Shares"><Share2 size={10} />{formatMetricValue(post.shares ?? 0)}</span>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="overflow-hidden rounded-lg border border-border bg-background"
      data-testid={`post-card-${post.postId}`}
    >
      <ThumbnailImage post={post} compact={false} />
      <div className="p-3">
        <div className="mb-1.5 flex items-center gap-1.5">
          <PlatformIcon size={14} className="shrink-0 text-muted-foreground" />
          <span className="text-[11px] text-muted-foreground">{platformLabel}</span>
          <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
            {POST_TYPE_LABELS[post.postType]}
          </span>
          <span className="ml-auto text-[10px] tabular-nums text-muted-foreground">{formatDate(post.createdAt)}</span>
        </div>
        <p className="mb-2 line-clamp-3 text-sm leading-snug" dir="auto">{captionExcerpt}</p>
        <div className="mb-2 grid grid-cols-3 gap-1 text-[11px] tabular-nums text-muted-foreground">
          <span className="flex items-center gap-1" title="Reactions"><Heart size={12} />{formatMetricValue(post.reactions)}</span>
          <span className="flex items-center gap-1" title="Comments"><MessageCircle size={12} />{formatMetricValue(post.comments)}</span>
          {isInstagram ? (
            <span className="flex items-center gap-1" title="Saves"><Bookmark size={12} />{formatMetricValue(post.saves ?? 0)}</span>
          ) : (
            <span className="flex items-center gap-1" title="Shares"><Share2 size={12} />{formatMetricValue(post.shares ?? 0)}</span>
          )}
          <span className="flex items-center gap-1" title="Impressions"><Eye size={12} />{formatMetricValue(post.impressions)}</span>
          <span className="flex items-center gap-1" title="Clicks"><MousePointer size={12} />{formatMetricValue(post.clicks)}</span>
        </div>
        <a
          href={post.permalinkUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-0.5 text-[10px] text-primary hover:underline"
        >
          View post <ExternalLink size={10} />
        </a>
      </div>
    </div>
  );
}

export function TopPostsWidget({ widget, data, onAutoResize }: TopPostsWidgetProps) {
  const config = widget.config as { postCount?: number; platform?: string };
  const colSpan = widget.position.colSpan;
  const postCount = config.postCount ?? 5;
  const gridCols = getPostGridCols(colSpan);
  const compact = gridCols === 1;
  const { shell } = useChartWidgetShell(widget.config);
  const gridColsClass = gridCols === 3 ? "grid-cols-3" : gridCols === 2 ? "grid-cols-2" : "grid-cols-1";

  // Auto-resize based on content
  useEffect(() => {
    if (onAutoResize && data?.data && isTopPostsData(data.data)) {
      const actualPosts = data.data.posts.length;
      const newRowSpan = Math.min(Math.ceil(actualPosts / gridCols) * 2, 8);
      onAutoResize(widget.id, newRowSpan);
    }
  }, [data?.data, gridCols, onAutoResize, widget.id]);

  // Loading state
  if (data?.loading) {
    return shell(
      "top-posts",
      widget.title,
      <div className={`grid flex-1 gap-2 ${gridColsClass}`}>
        {Array.from({ length: postCount }).map((_, i) => (
          <div key={i} className="animate-pulse">
            {compact ? (
              <div className="flex gap-3 rounded-lg border border-border p-2.5">
                <div className="h-16 w-16 shrink-0 rounded bg-muted" />
                <div className="flex-1 space-y-2 py-1">
                  <div className="h-3 w-3/4 rounded bg-muted" />
                  <div className="h-3 w-1/2 rounded bg-muted" />
                </div>
              </div>
            ) : (
              <div className="overflow-hidden rounded-lg border border-border">
                <div className="aspect-square w-full bg-muted" />
                <div className="space-y-2 p-3">
                  <div className="h-3 w-1/2 rounded bg-muted" />
                  <div className="h-3 w-3/4 rounded bg-muted" />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>,
    );
  }

  // Error state
  if (data?.error) {
    return shell(
      "top-posts",
      widget.title,
      <div className="flex flex-col items-center gap-2">
        <AlertCircle size={24} className="text-destructive" />
        <ChartMessage role="alert">{data.error}</ChartMessage>
      </div>,
      { centered: true },
    );
  }

  const topPostsData = data?.data;

  // No data state
  if (!topPostsData || !isTopPostsData(topPostsData) || topPostsData.posts.length === 0) {
    return shell(
      "top-posts",
      widget.title,
      <div className="flex flex-col items-center gap-2 text-center" data-testid="top-posts-empty">
        <span className="text-sm text-muted-foreground">
          Connect Facebook, Instagram, or LinkedIn to see top posts
        </span>
        <span className="text-xs text-muted-foreground">
          Integrations setup coming in Epic 9
        </span>
      </div>,
      { centered: true },
    );
  }

  // Data state
  return shell(
    "top-posts",
    widget.title,
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div className={`grid auto-rows-min gap-2 ${gridColsClass}`}>
        {topPostsData.posts.map((post) => (
          <PostCard
            key={`${post.platform}-${post.postId}`}
            post={post}
            compact={compact}
          />
        ))}
      </div>
    </div>,
  );
}
